/* eslint-disable react/jsx-props-no-spreading */

'use client';

import { c as _c } from "react/compiler-runtime";
import Icon, { DownOutlined } from '@ant-design/icons';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/css-class';
import type { ComponentProps } from 'react';
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-80 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive", {
  variants: {
    variant: {
      default: 'bg-primary-9 text-white shadow-xs hover:bg-primary-9/90',
      destructive: 'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
      success: 'bg-accent-dark text-white shadow-xs hover:bg-accent-dark/90 focus-visible:ring-accent-light/20 disabled:text-white!',
      outline: 'border bg-white text-primary-9 border-neutral-2 shadow-xs hover:bg-primary-9 hover:text-white active:bg-primary-9 focus-visible:border-primary-6 focus-visible:ring-primary-6/40',
      field: 'border bg-white text-primary-9 border-neutral-2 shadow-xs hover:border-primary-8 focus-visible:border-primary-6 focus-visible:ring-primary-6/40',
      ghost: 'hover:bg-neutral-1 hover:text-primary-9',
      link: 'text-primary underline-offset-4 hover:underline',
      icon: '',
      shadow: cn('flex w-full px-8 py-6 bg-linear-to-r from-primary-8 to-primary-10 text-white', 'hover:from-primary-9 hover:to-primary-10/70')
    },
    size: {
      default: 'h-9 px-4 py-2 has-[>svg]:px-3',
      sm: 'h-8 rounded-sm gap-1.5 text-sm py-3 px-3 has-[>svg]:px-2.5',
      md: 'h-10 rounded-md gap-1.5 text-base py-3 px-4 has-[>svg]:px-3',
      lg: 'h-12 rounded-lg py-3 px-6 text-lg has-[>svg]:px-4',
      responsive: `h-10 rounded-md gap-1.5 text-md py-3 px-4 has-[>svg]:px-3 xl:h-12 xl:rounded-lg xl:py-3 xl:px-6 xl:text-lg xl:has-[>svg]:px-4`
    },
    active: {
      true: '',
      false: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  },
  compoundVariants: [{
    active: true,
    variant: 'default',
    className: ''
  }, {
    active: true,
    variant: 'outline',
    className: 'bg-primary-9 text-white'
  }, {
    active: true,
    variant: 'ghost',
    className: 'bg-neutral-1 text-primary-9 font-semibold'
  }, {
    size: 'sm',
    variant: 'icon',
    className: 'h-8 w-8 size-8!'
  }, {
    size: 'md',
    variant: 'icon',
    className: 'h-10 w-10 size-10!'
  }, {
    size: 'lg',
    variant: 'icon',
    className: 'h-12 w-12 size-12!'
  }]
});
function Button(t0) {
  const $ = _c(22);
  let active;
  let borderless;
  let className;
  let props;
  let ref;
  let rounded;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      ref,
      rounded,
      borderless,
      active,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = active;
    $[2] = borderless;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
    $[6] = rounded;
    $[7] = size;
    $[8] = t1;
    $[9] = variant;
  } else {
    active = $[1];
    borderless = $[2];
    className = $[3];
    props = $[4];
    ref = $[5];
    rounded = $[6];
    size = $[7];
    t1 = $[8];
    variant = $[9];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  let t2;
  if ($[10] !== active || $[11] !== borderless || $[12] !== className || $[13] !== rounded || $[14] !== size || $[15] !== variant) {
    t2 = cn(buttonVariants({
      variant,
      size,
      active,
      className
    }), {
      "rounded-full!": rounded,
      "border-none!": borderless
    });
    $[10] = active;
    $[11] = borderless;
    $[12] = className;
    $[13] = rounded;
    $[14] = size;
    $[15] = variant;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== Comp || $[18] !== props || $[19] !== ref || $[20] !== t2) {
    t3 = <Comp ref={ref} role="button" data-slot="button" className={t2} {...props} />;
    $[17] = Comp;
    $[18] = props;
    $[19] = ref;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
}
interface ButtonArrowProps extends React.SVGProps<SVGSVGElement> {
  icon?: typeof Icon;
}
function ButtonArrow(t0) {
  const $ = _c(5);
  const {
    icon: t1,
    className
  } = t0;
  const UIcon = t1 === undefined ? DownOutlined : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("ms-auto -me-1", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== UIcon || $[3] !== t2) {
    t3 = <Icon component={UIcon} data-slot="button-arrow" className={t2} />;
    $[2] = UIcon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export { Button, ButtonArrow, buttonVariants };